import React, { useState, useMemo, useEffect } from 'react'
import Head from 'next/head'
import Link from 'next/link'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Bot, Trophy, ArrowRight,
  BarChart3, Calendar, Lock, Sparkles,
  ChevronDown, ChevronUp, Unlock
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import UnlockModal from '../components/UnlockModal'

// ─── Error Boundary ───────────────────────────────────────────────────────────
class SportsHubErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, errorMsg: '' }
  }
  static getDerivedStateFromError(err) {
    return { hasError: true, errorMsg: err?.message || String(err) || 'Unknown error' }
  }
  componentDidCatch(err, info) {
    console.log('[SportsHub] Error caught:', err?.name, err?.message, err?.stack)
    console.log('[SportsHub] Component stack:', info?.componentStack)
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#0B0F1A] text-white flex items-center justify-center p-8">
          <div className="text-center max-w-md">
            <p className="text-red-400 text-sm mb-2">Sports Hub failed to load.</p>
            <p className="text-white/40 text-xs font-mono">{this.state.errorMsg}</p>
            <button onClick={() => this.setState({ hasError: false })} className="mt-4 px-4 py-2 bg-[#3d7fff] rounded-lg text-sm hover:bg-[#6b9cff]">
              Retry
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

const PERIOD_OPTIONS = [
  { value: 'all',   label: 'All Time' },
  { value: 'month', label: 'This Month' },
  { value: 'week',  label: 'This Week' },
  { value: 'today', label: 'Today' },
]

// ─── helpers ──────────────────────────────────────────────────────────────────

function edgePct(prob) { return Math.max(0, Math.round((prob || 52) - 52)) }

function valueScore(prob) { return Math.min(9.9, (prob || 50) / 10).toFixed(1) }

function confLabel(prob) {
  if (prob >= 75) return { text: 'High',   cls: 'text-green-400 bg-green-500/20 border-green-500/40' }
  if (prob >= 60) return { text: 'Medium', cls: 'text-yellow-400 bg-yellow-500/20 border-yellow-500/40' }
  return              { text: 'Low',    cls: 'text-red-400 bg-red-500/20 border-red-500/40' }
}

function formatKickoff(str) {
  if (!str) return null
  try {
    return new Date(str).toLocaleString('en-ZA', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
    })
  } catch { return null }
}

// ─── fetch helpers ────────────────────────────────────────────────────────────

async function fetchBotStats() {
  // ?v=2 busts any proxy/browser cache that has the old stats shape (pre-refactor)
  const res = await fetch('/api/bot/stats?v=2', { cache: 'no-store' })
  if (!res.ok) throw new Error('SkillBot performance is temporarily unavailable')
  const raw = await res.json()
  if (raw.message === 'Bot not yet initialized') return null
  // Read flat top-level fields (proxy-safe) — same path as roi which always works
  return {
    isOnline:       true,
    hitRate:        raw.hit_rate    ?? raw.stats?.hit_rate   ?? 0,
    totalBets:      raw.total_bets  ?? raw.stats?.total_all  ?? 0,
    totalProfitZar: raw.profit_zar  ?? raw.stats?.profit_zar ?? 0,
    currentStreak:  raw.streak      ?? 0,
    avgOdds:        raw.avg_odds    ?? raw.stats?.avg_odds   ?? 0,
    wins:           raw.wins        ?? raw.stats?.wins       ?? 0,
    roi:            raw.roi         ?? 0,
  }
}

async function fetchRecentBets() {
  const res = await fetch('/api/bot/recent-bets?limit=10')
  if (!res.ok) throw new Error('SkillBot activity is temporarily unavailable')
  const data = await res.json()
  const bets = data.bets || data || []
  return bets.slice(0, 10).map(b => ({
    id:            b.id,
    match:         b.match,
    homeTeam:      b.home_team  || (b.match ? b.match.split(' vs ')[0]?.trim() : 'Home'),
    awayTeam:      b.away_team  || (b.match ? b.match.split(' vs ')[1]?.trim() : 'Away'),
    homeLogo:      b.home_logo || null,
    awayLogo:      b.away_logo || null,
    selection:     b.pick,
    stakeZar:      (b.stake_zar || 0) * 100,
    odds:          (b.odds      || 0) * 100,
    status:        b.status,
    aiProbability: b.ai_probability ?? null,
    league:        b.league         || 'Unknown League',
    kickoff:       b.kickoff,
    placedAt:      b.placed_at || b.created_at || null,
  }))
}

async function fetchComparison(userId, period) {
  if (!userId) return null
  const res = await fetch(`/api/bot/compare/${userId}?period=${period}`)
  if (!res.ok) throw new Error('Comparison is temporarily unavailable')
  const cmp = await res.json()
  return {
    botMessage: cmp.bot_message || null,
    userStats: {
      hitRate:   cmp.user?.hit_rate   || 0,
      wins:      cmp.user?.wins       || 0,
      profitZar: cmp.user?.profit_zar || 0,
      avgOdds:   cmp.user?.avg_odds   || 0,
      totalBets: cmp.user?.total_bets || 0,
    },
    botStats: cmp.bot ? {
      hitRate:        cmp.bot?.hit_rate   || 0,
      wins:           cmp.bot?.wins       || 0,
      totalProfitZar: cmp.bot?.profit_zar || 0,
      avgOdds:        cmp.bot?.avg_odds   || 0,
      totalBets:      cmp.bot?.total_bets || 0,
    } : null,
  }
}

// ─── StatusTile ───────────────────────────────────────────────────────────────

function StatusTile({ label, value, isLocked = false }) {
  return (
    <div className="bg-white/5 border border-white/8 rounded-xl p-3 relative overflow-hidden">
      <div className="text-[9px] text-white/40 uppercase tracking-wider mb-1 leading-tight">{label}</div>
      <div className={`text-2xl font-extrabold text-white ${isLocked ? 'blur-sm select-none' : ''}`}>{value}</div>
      {isLocked && (
        <div className="absolute top-2 right-2">
          <Lock className="w-3.5 h-3.5 text-white/30" />
        </div>
      )}
    </div>
  )
}

// ─── FeedCard ─────────────────────────────────────────────────────────────────

function FeedCard({ bet, isPremium, isBasic, isUnlocked, onUnlock, isSelected, onClick }) {
  const isLocked    = !isPremium && !isBasic && !isUnlocked
  const isCompleted = bet.status === 'won' || bet.status === 'lost'
  const isPending   = bet.status === 'pending'

  const statusCfg = isPending
    ? { label: 'Pending',   dotCls: 'bg-yellow-400 animate-pulse', badgeCls: 'text-yellow-300 bg-yellow-500/15 border-yellow-500/30' }
    : isCompleted
    ? { label: 'Completed', dotCls: 'bg-blue-400',                  badgeCls: 'text-blue-300  bg-blue-500/15  border-blue-500/30'  }
    : { label: 'Triggered', dotCls: 'bg-green-400',                 badgeCls: 'text-green-300 bg-green-500/15 border-green-500/30' }

  const hasConfidence = Number.isFinite(bet.aiProbability)
  const conf = hasConfidence ? confLabel(bet.aiProbability) : null
  const edge = hasConfidence ? edgePct(bet.aiProbability) : null
  const cost = 1

  return (
    <div
      onClick={onClick}
      className={`sports-hub-feed-card rounded-xl border p-3 cursor-pointer transition-all ${
        isSelected
          ? 'is-selected border-blue-500/70 bg-blue-500/10'
          : 'border-white/8 bg-white/3 hover:border-white/15 hover:bg-white/5'
      }`}
    >
      {/* Row 1: live dot + league + status */}
      <div className="sports-hub-feed-card__status flex items-center gap-2 mb-1.5">
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${statusCfg.dotCls}`} />
        <span className="text-[10px] text-white/40 truncate flex-1">{bet.league}</span>
        <span className={`text-[9px] px-1.5 py-0.5 rounded border ${statusCfg.badgeCls}`}>{statusCfg.label}</span>
      </div>

      {/* Row 2: teams */}
      <div className="sports-hub-feed-card__teams flex items-center gap-1.5 mb-2">
        <span className="sports-hub-feed-card__team">
          {bet.homeLogo
            ? <img src={bet.homeLogo} alt="" className="w-5 h-5 object-contain flex-shrink-0" />
            : <span className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center text-[9px] font-bold flex-shrink-0">{bet.homeTeam?.[0]}</span>
          }
          <span className="sports-hub-feed-card__team-name text-sm font-semibold text-white">{bet.homeTeam}</span>
        </span>
        <span className="text-white/30 text-xs flex-shrink-0">vs</span>
        <span className="sports-hub-feed-card__team">
          {bet.awayLogo
            ? <img src={bet.awayLogo} alt="" className="w-5 h-5 object-contain flex-shrink-0" />
            : <span className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center text-[9px] font-bold flex-shrink-0">{bet.awayTeam?.[0]}</span>
          }
          <span className="sports-hub-feed-card__team-name text-sm font-semibold text-white">{bet.awayTeam}</span>
        </span>
      </div>

      {/* Row 3: edge / confidence / kickoff */}
      <div className="sports-hub-feed-card__meta flex items-center gap-2 text-[11px] mb-2 flex-wrap">
        <span className="text-white/40">EDGE <span className="text-green-400 font-bold">{edge === null ? '—' : `+${edge}%`}</span></span>
        <span className="text-white/40">CONF <span className={`font-bold ${conf ? conf.cls.split(' ')[0] : 'text-white/40'}`}>{hasConfidence ? `${bet.aiProbability}%` : '—'}</span>
          {conf && <span className={`ml-1 text-[9px] px-1.5 py-0.5 rounded border ${conf.cls}`}>{conf.text}</span>}
        </span>
        {bet.kickoff && (
          <span className="sports-hub-feed-card__kickoff text-white/25 ml-auto text-[9px]">Sim: {formatKickoff(bet.kickoff)}</span>
        )}
      </div>

      {/* Row 4: action */}
      {isLocked ? (
        <button
          onClick={e => { e.stopPropagation(); onUnlock && onUnlock(bet) }}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#FFD700] to-yellow-500 text-black text-[11px] font-bold hover:brightness-110 transition"
        >
          <Lock className="w-3 h-3" />
          {isCompleted ? 'Unlock Results' : 'Unlock Insight'}
          <span className="ml-1 flex items-center gap-0.5 bg-black/20 rounded px-1">{cost} coin</span>
        </button>
      ) : (
        <div className={`sports-hub-feed-card__action text-xs px-2.5 py-1.5 rounded-lg inline-flex items-center gap-1.5 ${
          bet.status === 'won'  ? 'bg-green-500/20 text-green-400' :
          bet.status === 'lost' ? 'bg-red-500/20 text-red-400'     :
                                  'bg-blue-500/20 text-blue-300'
        }`}>
          {bet.selection || bet.status}
          {bet.odds > 0 && <span className="text-white/40">@ {(bet.odds / 100).toFixed(2)}</span>}
        </div>
      )}
    </div>
  )
}

// ─── ComparisonBar ────────────────────────────────────────────────────────────

function ComparisonBar({ label, userValue, botValue, isPercentage = false, isProfit = false }) {
  const maxV     = Math.max(Math.abs(userValue), Math.abs(botValue || 0), 1)
  const userW    = Math.min(Math.abs(userValue) / maxV * 100, 100)
  const botW     = Math.min(Math.abs(botValue  || 0) / maxV * 100, 100)
  const fmt      = v => isPercentage ? `${v?.toFixed(1) || 0}%` : isProfit ? `R${v?.toFixed(0) || 0}` : v || 0
  const userClr  = isProfit ? (userValue >= 0  ? 'bg-blue-500'   : 'bg-red-500') : 'bg-blue-500'
  const botClr   = isProfit ? ((botValue || 0) >= 0 ? 'bg-[#e8b23d]' : 'bg-red-500') : 'bg-[#e8b23d]'

  return (
    <div className="mb-3">
      <div className="text-[10px] text-white/40 mb-1.5">{label}</div>
      <div className="space-y-1.5">
        {[
          { label: 'You',     w: userW, color: userClr, val: userValue },
          { label: 'SkillBot', w: botW,  color: botClr,  val: botValue },
        ].map(row => (
          <div key={row.label} className="flex items-center gap-2">
            <div className="w-14 text-[10px] text-white/40 flex items-center gap-1">
              {row.label === 'SkillBot' && <Bot className="w-3 h-3 text-[#e8b23d]" />}
              <span className={row.label === 'SkillBot' ? 'text-[#e8b23d]' : ''}>{row.label}</span>
            </div>
            <div className="flex-1 h-5 bg-white/5 rounded-full overflow-hidden">
              <div
                className={`h-full ${row.color} rounded-full flex items-center justify-end px-2 transition-all duration-500`}
                style={{ width: `${row.w}%` }}
              >
                <span className="text-[10px] font-bold text-white whitespace-nowrap">{fmt(row.val)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

function SportsHubContent() {
  const { user: authUser } = useAuth()
  const currentUserId = authUser?.user_id
  const isPremium     = authUser?.subscription_tier?.toLowerCase() === 'premium'
  const isBasic       = authUser?.subscription_tier?.toLowerCase() === 'basic'
  const queryClient   = useQueryClient()

  const [period,        setPeriod       ] = useState('all')
  const [feedTab,       setFeedTab      ] = useState('feed')
  const [selectedIdx,   setSelectedIdx  ] = useState(0)
  const [insightOpen,   setInsightOpen  ] = useState(false)

  // ── Unlock state ─────────────────────────────────────────────────────────────
  const [unlockedInsights, setUnlockedInsights] = useState(new Set())
  const [unlockModal, setUnlockModal] = useState({ isOpen: false, matchId: null })
  const unlockStorageKey = currentUserId ? `sports-hub-unlocked:${currentUserId}` : null

  useEffect(() => {
    if (!unlockStorageKey) {
      setUnlockedInsights(new Set())
      return
    }
    try {
      const stored = localStorage.getItem(unlockStorageKey)
      setUnlockedInsights(stored ? new Set(JSON.parse(stored)) : new Set())
    } catch {
      setUnlockedInsights(new Set())
    }
  }, [unlockStorageKey])

  // ── Hydrate unlocks from server so they survive cache-clears / device switches
  const { data: serverUnlocks } = useQuery({
    queryKey: ['skillcoins', 'unlocks'],
    queryFn:  () => fetch('/api/skillcoins/unlocks', { credentials: 'include' }).then(r => r.ok ? r.json() : null),
    enabled:  !!currentUserId,
    staleTime: 60 * 1000,
  })
  useEffect(() => {
    if (!serverUnlocks) return
    const named = serverUnlocks.named_unlocks || []
    const persisted = (serverUnlocks.unlocks || [])
      .flatMap(unlock => [unlock.unlock_type, unlock.unlock_key])
      .filter(Boolean)
    // Bot insights are non-date-keyed records, so restore them from raw persisted unlocks too.
    const botKeys = [...new Set([...named, ...persisted].filter(k => k.startsWith('bot-insight:')))]
    if (botKeys.length === 0) return
    setUnlockedInsights(prev => {
      const merged = new Set([...prev, ...botKeys])
      if (merged.size === prev.size) return prev   // nothing new — avoid re-render
      try { if (unlockStorageKey) localStorage.setItem(unlockStorageKey, JSON.stringify([...merged])) } catch {}
      return merged
    })
  }, [serverUnlocks, unlockStorageKey])

  const handleOpenUnlock = (bet) => {
    setUnlockModal({ isOpen: true, matchId: `bot-insight:${bet.id}` })
  }
  const handleUnlocked = (matchId) => {
    setUnlockedInsights(prev => {
      const next = new Set([...prev, matchId])
      try { if (unlockStorageKey) localStorage.setItem(unlockStorageKey, JSON.stringify([...next])) } catch {}
      return next
    })
    queryClient.invalidateQueries({ queryKey: ['skillcoins', 'unlocks'] })
  }
  const insightUnlockKey  = selectedBet => selectedBet ? `bot-insight:${selectedBet.id}` : null
  const isBetUnlocked     = (bet) => isPremium || isBasic || unlockedInsights.has(`bot-insight:${bet?.id}`)

  const { data: botStats, isLoading: statsLoading, isError: statsError } = useQuery({
    queryKey: ['bot', 'stats'],
    queryFn:  fetchBotStats,
    staleTime: 30 * 1000,          // retry sooner if null came back
    refetchInterval: 60 * 1000,    // poll every minute so it self-heals
    refetchOnWindowFocus: true,
  })

  const { data: _recentBetsRaw = [], isLoading: betsLoading, isError: betsError } = useQuery({
    queryKey: ['bot', 'recent-bets', 'v2'],   // bumped key to flush old non-array cached value
    queryFn:  fetchRecentBets,
    staleTime: 2 * 60 * 1000,
  })
  // Defensive: stale cache from the old page could hold { bets: [...] } instead of an array
  const recentBets = Array.isArray(_recentBetsRaw) ? _recentBetsRaw : []

  const { data: comparisonData, isLoading: comparisonLoading, isError: comparisonError } = useQuery({
    queryKey: ['bot', 'compare', currentUserId, period],
    queryFn:  () => fetchComparison(currentUserId, period),
    enabled:  !!currentUserId,
    staleTime: 60 * 1000,
  })

  const userStats      = comparisonData?.userStats || {}
  const botCompareStats = comparisonData?.botStats || botStats || {}
  const statsUnavailable = statsLoading || statsError || !botStats
  const activityUnavailable = betsLoading || betsError

  const pendingCount   = recentBets.filter(b => b.status === 'pending').length
  const completedCount = recentBets.filter(b => b.status === 'won' || b.status === 'lost').length
  const todayCount     = recentBets.filter(b => {
    try { return b.placedAt && new Date(b.placedAt).toDateString() === new Date().toDateString() } catch { return false }
  }).length
  const edgeCount = recentBets.filter(
    b => Number.isFinite(b.aiProbability) && edgePct(b.aiProbability) > 0
  ).length

  const filteredBets = useMemo(() => {
    if (feedTab === 'pending')   return recentBets.filter(b => b.status === 'pending')
    if (feedTab === 'completed') return recentBets.filter(b => b.status === 'won' || b.status === 'lost')
    return recentBets
  }, [recentBets, feedTab])

  const selectedBet = filteredBets[selectedIdx] || null

  return (
    <>
      <Head>
        <title>Sports Hub – SkillBot Activity Center | SkillBets</title>
        <meta name="description" content="SkillBot's live activity feed, AI insights, and performance stats. See how you stack up against the AI." />
      </Head>

      <div className="sports-hub-page min-h-screen bg-[#0B0F1A] text-white pb-24" data-testid="sports-hub-page">
        <div className="sports-hub-shell max-w-[1440px] mx-auto px-3 sm:px-4 py-6" data-testid="sports-hub-layout">

          {/* ── Page header ─────────────────────────────────────────────── */}
          <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="sports-hub-mark w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white" data-testid="text-sports-hub-title">Sports Hub</h1>
                <p className="text-xs text-white/40">Can you beat SkillBot?</p>
              </div>
            </div>
            {authUser && (
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-white/30" />
                <select
                  value={period}
                  onChange={e => setPeriod(e.target.value)}
                  className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  data-testid="select-period"
                >
                  {PERIOD_OPTIONS.map(o => <option key={o.value} value={o.value} className="bg-white text-gray-900">{o.label}</option>)}
                </select>
              </div>
            )}
          </div>

          {/* ── 3-column grid ────────────────────────────────────────────── */}
          <div className="sports-hub-main grid grid-cols-1 lg:grid-cols-[260px_1fr_300px] gap-4 mb-5">

            {/* ════ LEFT PANEL ════════════════════════════════════════════ */}
            <div className="flex flex-col gap-4">

              {/* SkillBot Status */}
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[11px] font-bold text-white/60 uppercase tracking-wider">SkillBot Status</span>
                  <span className={`flex items-center gap-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                    statsError ? 'bg-red-500/15 text-red-400 border-red-500/30' : 'bg-green-500/15 text-green-400 border-green-500/30'
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${statsError ? 'bg-red-400' : 'bg-green-400 animate-pulse'}`} />
                    {statsError ? 'UNAVAILABLE' : statsLoading ? 'CONNECTING' : botStats ? 'LIVE' : 'INITIALIZING'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <StatusTile label="Current Picks"    value={activityUnavailable ? '—' : pendingCount} />
                  <StatusTile label="Pending Analysis" value={activityUnavailable ? '—' : pendingCount} />
                  <StatusTile label="Today's Triggers" value={activityUnavailable ? '—' : todayCount} />
                  <StatusTile label="Live Edges Found" value={activityUnavailable ? '—' : edgeCount} />
                </div>
              </div>

              {/* SkillBot Performance */}
              <div className="sports-hub-performance rounded-2xl border p-4">
                <div className="text-[11px] font-bold text-white/50 uppercase tracking-wider mb-3">
                  SkillBot Performance <span className="text-white/25 normal-case font-normal">(All Time)</span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 mb-3">
                      {[
                    { label: 'Win Rate', val: statsUnavailable ? '—' : `${botStats.hitRate?.toFixed(0) || 0}%`,  color: 'text-white',   size: 'text-2xl' },
                    { label: 'ROI',      val: statsUnavailable ? '—' : `${botStats.roi >= 0 ? '+' : ''}${botStats.roi?.toFixed(1) || 0}%`, color: (botStats?.roi || 0) >= 0 ? 'text-green-400' : 'text-red-400', size: 'text-2xl' },
                    { label: 'Total Sims', val: statsUnavailable ? '—' : (botStats.totalBets || 0).toLocaleString(), color: 'text-white', size: 'text-xl' },
                    { label: 'Profit',   val: statsUnavailable ? '—' : `R${botStats.totalProfitZar?.toFixed(0) || 0}`, color: (botStats?.totalProfitZar || 0) >= 0 ? 'text-green-400' : 'text-red-400', size: 'text-xl' },
                  ].map(s => (
                    <div key={s.label}>
                      <div className="text-[10px] text-white/35 mb-0.5">{s.label}</div>
                      <div className={`${s.size} font-extrabold ${s.color}`}>{s.val}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Unlock Everything CTA */}
              {!isPremium && !isBasic && (
                <div className="rounded-2xl border border-[#FFD700]/25 bg-gradient-to-br from-yellow-900/20 to-orange-900/10 p-4">
                <div className="flex items-center gap-2 mb-2">
                     <Trophy className="w-4 h-4 text-[#FFD700]" />
                    <span className="text-xs font-bold text-[#FFD700] uppercase tracking-wide">Unlock Everything</span>
                  </div>
                  <p className="text-[11px] text-white/50 mb-3 leading-relaxed">
                    Upgrade to Premium to unlock SkillBot's full activity feed, AI insights, and historical results — all in one go.
                  </p>
                  <Link
                    href="/profile#subscription"
                    className="flex items-center justify-center gap-1.5 w-full py-2 rounded-xl bg-gradient-to-r from-[#FFD700] to-yellow-500 text-black text-xs font-bold hover:brightness-110 transition"
                  >
                    Upgrade to Premium
                  </Link>
                </div>
              )}
            </div>

            {/* ════ CENTRE PANEL ══════════════════════════════════════════ */}
            <div className="flex flex-col gap-4">

              {/* Activity Center header */}
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="sports-hub-bot-mark w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                      <Bot className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="text-sm font-extrabold text-white uppercase tracking-wide">Skillbot Activity Center</div>
                      <div className="text-[11px] text-white/40">SkillBot is always scanning the market to find value.</div>
                    </div>
                  </div>
                </div>

                {/* 4-stat summary row */}
                <div className="grid grid-cols-4 gap-2 text-center">
                    {[
                      { label: 'CURRENT PICKS',    val: activityUnavailable ? '—' : pendingCount },
                      { label: 'PENDING ANALYSIS', val: activityUnavailable ? '—' : pendingCount },
                      { label: "TODAY'S TRIGGERS", val: activityUnavailable ? '—' : todayCount },
                      { label: 'LIVE EDGES FOUND', val: activityUnavailable ? '—' : edgeCount },
                   ].map(({ label, val }) => (
                    <div key={label} className="bg-white/5 rounded-xl p-2.5">
                      <div className="text-xl font-extrabold text-white mb-0.5">{val}</div>
                      <div className="text-[8px] text-white/35 whitespace-pre-line leading-tight">{label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tabs + feed cards */}
              <div className="sports-hub-feed-panel rounded-2xl border border-white/10 bg-white/[0.03] flex flex-col flex-1">
                {/* Tab bar */}
                <div className="sports-hub-feed-tabs flex items-center px-3 overflow-x-auto" role="tablist" aria-label="SkillBot activity">
                  {[
                    { id: 'feed',      label: 'SkillBot Feed' },
                    { id: 'pending',   label: `Pending Analysis${pendingCount > 0 ? ` (${pendingCount})` : ''}` },
                    { id: 'completed', label: `Completed Results${completedCount > 0 ? ` (${completedCount})` : ''}` },
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => { setFeedTab(tab.id); setSelectedIdx(0) }}
                      role="tab"
                      aria-selected={feedTab === tab.id}
                      aria-controls="sports-hub-feed-panel"
                      className={`py-3 px-3 text-xs font-semibold transition-colors whitespace-nowrap flex-shrink-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-3px] focus-visible:outline-blue-300 ${
                        feedTab === tab.id
                          ? 'text-blue-300'
                          : 'text-white/35 hover:text-white/60'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* Feed */}
                <div id="sports-hub-feed-panel" className="sports-hub-feed-list p-3 space-y-2 flex-1" role="tabpanel" data-testid="sports-hub-feed">
                  {betsLoading ? (
                    <div className="text-center py-10 text-white/40 text-sm">Loading SkillBot activity…</div>
                  ) : betsError ? (
                    <div className="text-center py-10 text-red-300 text-sm">SkillBot activity is temporarily unavailable.</div>
                  ) : filteredBets.length === 0 ? (
                    <div className="text-center py-10 text-white/25 text-sm">
                      No {feedTab === 'pending' ? 'pending' : feedTab === 'completed' ? 'completed' : ''} bets to show
                    </div>
                  ) : (
                    filteredBets.map((bet, i) => (
                      <FeedCard
                        key={bet.id || i}
                        bet={bet}
                        isPremium={isPremium}
                        isBasic={isBasic}
                        isUnlocked={unlockedInsights.has(`bot-insight:${bet.id}`)}
                        onUnlock={handleOpenUnlock}
                        isSelected={selectedIdx === i}
                        onClick={() => setSelectedIdx(i)}
                      />
                    ))
                  )}
                </div>

                <div className="px-3 pb-3 pt-1 border-t border-white/5">
                  <Link href="/history" className="flex items-center justify-center gap-1.5 text-xs text-blue-300 hover:text-blue-200 transition-colors">
                    View All SkillBot Activity <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>
            </div>

            {/* ════ RIGHT PANEL ═══════════════════════════════════════════ */}
            <div className="flex flex-col gap-4">

              {/* Selected Match Insight */}
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden">
                {/* Mobile accordion toggle */}
                <button
                  onClick={() => setInsightOpen(!insightOpen)}
                  className="w-full flex items-center justify-between p-4 lg:hidden"
                >
                  <span className="text-sm font-bold text-white">Selected Match Insight</span>
                  {insightOpen
                    ? <ChevronUp className="w-4 h-4 text-white/35" />
                    : <ChevronDown className="w-4 h-4 text-white/35" />
                  }
                </button>

                <div className={`${insightOpen ? 'block' : 'hidden'} lg:block`}>
                  <div className="px-4 pt-4 pb-0 hidden lg:flex items-center justify-between">
                    <span className="text-[11px] font-bold text-white/50 uppercase tracking-wider">Selected Match Insight</span>
                  </div>

                  {selectedBet ? (
                    <div className="p-4">
                      {/* League + date */}
                      <div className="flex items-center gap-1.5 text-[10px] text-white/35 mb-3">
                        <Trophy className="w-3 h-3 text-[#FFD700]" />
                        <span>{selectedBet.league}</span>
                        {selectedBet.kickoff && (
                          <span className="ml-auto">{formatKickoff(selectedBet.kickoff)}</span>
                        )}
                      </div>

                      {/* Teams */}
                      <div className="flex items-center justify-between gap-2 mb-4">
                        <div className="flex flex-col items-center gap-1.5 flex-1">
                          {selectedBet.homeLogo
                            ? <img src={selectedBet.homeLogo} alt="" className="w-10 h-10 object-contain" />
                            : <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-xs font-bold">{selectedBet.homeTeam?.[0]}</div>
                          }
                          <div className="text-xs font-semibold text-white text-center leading-tight">{selectedBet.homeTeam}</div>
                        </div>
                        <div className="text-sm font-extrabold text-white/30">VS</div>
                        <div className="flex flex-col items-center gap-1.5 flex-1">
                          {selectedBet.awayLogo
                            ? <img src={selectedBet.awayLogo} alt="" className="w-10 h-10 object-contain" />
                            : <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-xs font-bold">{selectedBet.awayTeam?.[0]}</div>
                          }
                          <div className="text-xs font-semibold text-white text-center leading-tight">{selectedBet.awayTeam}</div>
                        </div>
                      </div>

                      {/* Verdict */}
                      <div className="text-center mb-4">
                        <div className="text-[10px] text-white/35 uppercase mb-1">SkillBot Verdict</div>
                        <div className="text-base font-extrabold text-green-400 uppercase tracking-wide">
                          {selectedBet.selection || `${selectedBet.homeTeam} WIN`}
                        </div>
                      </div>

                      {/* Stats tiles */}
                      <div className="grid grid-cols-3 gap-1.5 mb-4">
                        {[
                          { label: 'Confidence', val: Number.isFinite(selectedBet.aiProbability) ? `${selectedBet.aiProbability}%` : '—', sub: Number.isFinite(selectedBet.aiProbability) ? confLabel(selectedBet.aiProbability).text : 'Unavailable', color: 'text-[#FFD700]' },
                          { label: 'Edge',        val: Number.isFinite(selectedBet.aiProbability) ? `+${edgePct(selectedBet.aiProbability)}%` : '—', sub: Number.isFinite(selectedBet.aiProbability) ? 'Vs Market' : 'Unavailable', color: 'text-green-400' },
                          { label: 'Value Score', val: Number.isFinite(selectedBet.aiProbability) ? `${valueScore(selectedBet.aiProbability)}/10` : '—', sub: Number.isFinite(selectedBet.aiProbability) ? 'Model score' : 'Unavailable', color: 'text-blue-400'  },
                        ].map(s => (
                          <div key={s.label} className="bg-white/5 rounded-xl p-2 text-center">
                            <div className="text-[9px] text-white/35 mb-1">{s.label}</div>
                            <div className={`text-sm font-extrabold ${s.color}`}>{s.val}</div>
                            <div className={`text-[8px] ${s.color} opacity-60`}>{s.sub}</div>
                          </div>
                        ))}
                      </div>

                      {isBetUnlocked(selectedBet) ? (
                        <div className="rounded-xl border border-green-500/25 bg-green-500/5 p-3 space-y-2" data-testid="sports-hub-full-report">
                          <div className="flex items-center gap-1.5 text-[10px] text-green-400 font-bold mb-1">
                            <Unlock className="w-3 h-3" /> Full AI Report
                          </div>
                          {[
                            { label: 'SkillBot Pick',    val: selectedBet.selection || '—' },
                            { label: 'Simulated Odds',   val: selectedBet.odds > 0 ? (selectedBet.odds / 100).toFixed(2) : 'N/A' },
                            { label: 'SkillBot Stake',   val: selectedBet.stakeZar > 0 ? `R${(selectedBet.stakeZar / 100).toFixed(0)}` : 'N/A' },
                            { label: 'AI Probability',   val: Number.isFinite(selectedBet.aiProbability) ? `${selectedBet.aiProbability}%` : 'Unavailable' },
                              { label: 'Outcome',          val: selectedBet.status === 'won' ? 'Won' : selectedBet.status === 'lost' ? 'Lost' : 'Pending' },
                          ].map(r => (
                            <div key={r.label} className="flex justify-between text-[11px]">
                              <span className="text-white/40">{r.label}</span>
                              <span className="text-white font-semibold">{r.val}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <button
                          onClick={() => handleOpenUnlock(selectedBet)}
                          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-[#FFD700] to-yellow-500 text-black text-xs font-bold hover:brightness-110 transition"
                          data-testid="sports-hub-unlock-report"
                        >
                          <Lock className="w-3 h-3" />
                          Unlock Full AI Report
          <span className="bg-black/20 rounded px-1.5 py-0.5 ml-1">1 coin</span>
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="p-6 text-center">
                      <Bot className="w-8 h-8 text-white/15 mx-auto mb-2" />
                      <p className="text-xs text-white/30">Select a match from the feed</p>
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>

          {/* ── BOTTOM ROW ──────────────────────────────────────────────── */}
          <div className="sports-hub-bottom grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">

            {/* Historical Performance */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <div className="text-[11px] font-bold text-white/50 uppercase tracking-wider mb-2">SkillBot Historical Performance</div>
              <div className="flex gap-4 mb-3 flex-wrap">
                  {[
                    { label: 'Win Rate', val: statsUnavailable ? '—' : `${botStats.hitRate?.toFixed(0) || 0}%`,  color: 'text-white' },
                    { label: 'ROI',      val: statsUnavailable ? '—' : `${botStats.roi >= 0 ? '+' : ''}${botStats.roi?.toFixed(1) || 0}%`, color: (botStats?.roi || 0) >= 0 ? 'text-green-400' : 'text-red-400' },
                    { label: 'Profit',   val: statsUnavailable ? '—' : `R${botStats.totalProfitZar?.toFixed(0) || 0}`, color: (botStats?.totalProfitZar || 0) >= 0 ? 'text-green-400' : 'text-red-400' },
                ].map(s => (
                  <div key={s.label}>
                    <div className="text-[9px] text-white/30">{s.label}</div>
                    <div className={`text-sm font-extrabold ${s.color}`}>{s.val}</div>
                  </div>
                ))}
              </div>
              <div className="border-t border-white/5 pt-3 text-[10px] text-white/30">
                All-time results from settled SkillBot simulations.
              </div>
            </div>

            {/* You vs SkillBot Challenge */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <div className="flex items-center gap-2 mb-1">
                <BarChart3 className="w-4 h-4 text-blue-400" />
                <span className="text-xs font-bold text-white">You vs SkillBot Challenge</span>
              </div>
              <p className="text-[10px] text-white/35 mb-3">Compete against SkillBot and climb the leaderboard.</p>

              {authUser && comparisonLoading ? (
                <div className="text-center py-8 text-xs text-white/35">Loading your comparison…</div>
              ) : authUser && comparisonError ? (
                <div className="text-center py-8 text-xs text-red-300">Comparison is temporarily unavailable.</div>
              ) : authUser && comparisonData ? (
                <>
                  <div className="flex items-end justify-between mb-4 gap-2">
                    <div className="text-center">
                       <div className="text-[9px] text-white/35 mb-1">YOU</div>
                      <div className="text-xl font-extrabold text-blue-400">{userStats.hitRate?.toFixed(0) || 0}%</div>
                      <div className="text-[9px] text-white/30">WIN RATE</div>
                    </div>
                    <div className="text-center pb-1">
                      <div className={`text-sm font-extrabold ${
                        (userStats.hitRate || 0) >= (botCompareStats.hitRate || 0)
                          ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {(userStats.hitRate || 0) >= (botCompareStats.hitRate || 0)
                          ? `+${Math.abs(Math.round((userStats.hitRate || 0) - (botCompareStats.hitRate || 0)))}%`
                          : `-${Math.abs(Math.round((botCompareStats.hitRate || 0) - (userStats.hitRate || 0)))}%`
                        }
                      </div>
                      <div className="text-[8px] text-white/25">
                        {(userStats.hitRate || 0) >= (botCompareStats.hitRate || 0) ? 'AHEAD' : 'BEHIND'} SKILLBOT
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-[9px] text-white/35 mb-1 flex items-center gap-1 justify-center"><Bot className="w-3 h-3" /> SKILLBOT</div>
                      <div className="text-xl font-extrabold text-[#e8b23d]">{botCompareStats.hitRate?.toFixed(0) || 0}%</div>
                      <div className="text-[9px] text-white/30">WIN RATE</div>
                    </div>
                  </div>
                  <ComparisonBar label="Win Rate"    userValue={userStats.hitRate    || 0} botValue={botCompareStats.hitRate        || 0} isPercentage />
                  <ComparisonBar label="Profit/Loss" userValue={userStats.profitZar  || 0} botValue={botCompareStats.totalProfitZar || 0} isProfit />
                  <div className="text-[10px] text-white/30 text-center pt-1">
                    {(userStats.hitRate || 0) >= (botCompareStats.hitRate || 0)
                       ? "You're beating SkillBot. Keep it up."
                      : "SkillBot is currently ahead. Analyze Pro Tips to improve!"}
                  </div>
                </>
              ) : (
                <div className="text-center py-4">
                  <p className="text-xs text-white/35 mb-3">Log in to see how you compare</p>
                   <Link href="/dashboard" className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#3d7fff] rounded-xl text-xs font-bold text-white hover:bg-[#6b9cff] transition">
                    Get Started <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              )}
            </div>

            {/* How It Works */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <div className="flex items-center gap-2 mb-3">
                 <Sparkles className="w-4 h-4 text-[#e8b23d]" />
                <span className="text-xs font-bold text-white">How It Works</span>
              </div>
              <div className="space-y-3">
                {[
                  'SkillBot scans 1000s of markets 24/7',
                  'Triggers high probability edges',
                  'Simulates outcomes with AI models',
                  'You compete against the machine',
                ].map((text, i) => (
                  <div key={i} className="flex items-start gap-3">
                     <div className="w-6 h-6 rounded-full bg-[#e8b23d]/15 border border-[#e8b23d]/40 flex items-center justify-center text-[11px] font-extrabold text-[#e8b23d] flex-shrink-0 mt-0.5">
                      {i + 1}
                    </div>
                    <p className="text-xs text-white/55 leading-snug">{text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Footer note */}
          <div className="mt-6 text-center text-[10px] text-white/20">
            SkillBot bets daily at 06:00 UTC (08:00 SAST) on matches with AI probability of 75% or higher.
            This is a simulation — no real money is wagered.
          </div>
        </div>
      </div>

      <UnlockModal
        isOpen={unlockModal.isOpen}
        onClose={() => setUnlockModal({ isOpen: false, matchId: null })}
        matchId={unlockModal.matchId}
        coinCost={1}
        onUnlocked={handleUnlocked}
      />
    </>
  )
}

export default function SportsHubPage() {
  return (
    <SportsHubErrorBoundary>
      <SportsHubContent />
    </SportsHubErrorBoundary>
  )
}
